/**
 * `/calendar` — recurring events + reminders (F2).
 *
 * A month grid (dots on days with due events) plus a list of all events
 * with add/edit/delete. Events are stored server-first via the `calendar`
 * UserDataStore namespace (`useServerBackedState`), so they survive a
 * browser clear, device switch, and `pi-deploy`. Recurrence math comes
 * from the shared pure engine. A "due today / overdue" banner + an
 * "enable notifications" hint satisfy decision D5's in-app channel.
 */
import { useMemo, useState } from 'react';

import {
  EMPTY_CALENDAR_DATA,
  describeRecurrence,
  isDueOn,
  type CalendarCategory,
  type CalendarData,
  type CalendarEvent,
  type Recurrence,
} from '@reefapp/shared';

import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { RecurrenceEditor } from '../calendar/RecurrenceEditor';
import { isoFromParts, localTodayIso } from '../calendar/localDate';
import { useDueReminders } from '../calendar/useDueReminders';
import { useServerBackedState } from '../hooks/useServerBackedState';
import { useBrowserNotifications } from '../notifications/useBrowserNotifications';

const CATEGORY_LABELS: ReadonlyArray<{ value: CalendarCategory; label: string }> = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'dosing', label: 'Dosing' },
  { value: 'test', label: 'Test' },
  { value: 'custom', label: 'Custom' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface EditorState {
  readonly mode: 'add' | 'edit';
  readonly event: CalendarEvent;
}

function blankEvent(): CalendarEvent {
  return {
    id: crypto.randomUUID(),
    title: '',
    category: 'maintenance',
    recurrence: { kind: 'weekly', days: [1] },
    createdAt: new Date().toISOString(),
  };
}

export function CalendarPage() {
  const { value: data, setValue, loading } =
    useServerBackedState<CalendarData>('calendar', EMPTY_CALENDAR_DATA);
  const { dueEvents, overdueEvents, count } = useDueReminders();
  const { permission, request, supported } = useBrowserNotifications();

  const [editor, setEditor] = useState<EditorState | null>(null);
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-based

  const events = data.events;

  // Build the calendar grid for the current month: leading blanks for the
  // weekday offset of the 1st, then each day-of-month cell.
  const monthCells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const leadBlanks = firstOfMonth.getDay(); // 0 = Sun
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: Array<{ day: number; iso: string } | null> = [];
    for (let i = 0; i < leadBlanks; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ day: d, iso: isoFromParts(viewYear, viewMonth + 1, d) });
    }
    return cells;
  }, [viewYear, viewMonth]);

  const dueCountOn = (iso: string): number =>
    events.filter((e) => isDueOn(e, iso)).length;

  const persist = (next: readonly CalendarEvent[]): void => {
    setValue({ events: next });
  };

  const saveEditor = (): void => {
    if (!editor) return;
    const trimmed = editor.event.title.trim();
    if (trimmed.length === 0) return;
    const event: CalendarEvent = { ...editor.event, title: trimmed };
    if (editor.mode === 'add') {
      persist([...events, event]);
    } else {
      persist(events.map((e) => (e.id === event.id ? event : e)));
    }
    setEditor(null);
  };

  const deleteEvent = (id: string): void => {
    persist(events.filter((e) => e.id !== id));
  };

  const prevMonth = (): void => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };
  const nextMonth = (): void => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const todayIso = localTodayIso();

  return (
    <div className="min-h-screen px-6 py-8" data-testid="calendar-page">
      <header className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Calendar &amp; Reminders</h1>
        <button
          type="button"
          onClick={() => setEditor({ mode: 'add', event: blankEvent() })}
          className="reefapp-btn-secondary text-sm"
          data-testid="calendar-add-event"
        >
          + Add event
        </button>
      </header>

      {count > 0 ? (
        <div
          className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
          data-testid="calendar-due-banner"
        >
          {overdueEvents.length > 0
            ? `${overdueEvents.length} overdue · `
            : ''}
          {dueEvents.length} due today
        </div>
      ) : null}

      {supported && permission !== 'granted' ? (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-reef-700 bg-reef-900/60 px-3 py-2 text-sm text-reef-300"
          data-testid="calendar-enable-notifications"
        >
          <span>
            Enable browser notifications to get a pop-up when a reminder is
            due.
          </span>
          <button
            type="button"
            onClick={() => void request()}
            className="reefapp-btn-secondary text-xs"
            data-testid="calendar-request-permission"
          >
            Enable notifications
          </button>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[2fr_3fr]">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <button
              type="button"
              onClick={prevMonth}
              className="rounded px-2 py-1 text-reef-300 hover:bg-reef-800"
              data-testid="calendar-prev-month"
              aria-label="Previous month"
            >
              ←
            </button>
            <CardTitle data-testid="calendar-month-label">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </CardTitle>
            <button
              type="button"
              onClick={nextMonth}
              className="rounded px-2 py-1 text-reef-300 hover:bg-reef-800"
              data-testid="calendar-next-month"
              aria-label="Next month"
            >
              →
            </button>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-reef-400 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthCells.map((cell, idx) => {
                if (cell === null) {
                  return <div key={`blank-${idx}`} />;
                }
                const due = dueCountOn(cell.iso);
                const isToday = cell.iso === todayIso;
                return (
                  <div
                    key={cell.iso}
                    className={
                      'flex h-12 flex-col items-center justify-center rounded text-sm ' +
                      (isToday
                        ? 'border border-reef-400 bg-reef-500/20 text-reef-100'
                        : 'text-reef-300')
                    }
                    data-testid={`calendar-cell-${cell.iso}`}
                  >
                    <span>{cell.day}</span>
                    {due > 0 ? (
                      <span
                        className="mt-0.5 h-1.5 w-1.5 rounded-full bg-amber-400"
                        data-testid={`calendar-dot-${cell.iso}`}
                        title={`${due} due`}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All events</CardTitle>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-sm text-reef-400">Loading…</p>
            ) : events.length === 0 ? (
              <p className="text-sm text-reef-400" data-testid="calendar-empty">
                No events yet. Add a recurring water change, sock swap, or
                test reminder.
              </p>
            ) : (
              <ul className="flex flex-col gap-2" data-testid="calendar-event-list">
                {events.map((e) => (
                  <li
                    key={e.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-reef-700 bg-reef-950/50 px-3 py-2"
                    data-testid={`calendar-event-${e.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-reef-700 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-reef-200">
                          {e.category}
                        </span>
                        <span className="truncate text-sm text-reef-100">
                          {e.title}
                        </span>
                      </div>
                      <div className="text-xs text-reef-400">
                        {describeRecurrence(e.recurrence)}
                        {e.time ? ` · ${e.time}` : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => setEditor({ mode: 'edit', event: e })}
                        className="text-xs text-reef-300 hover:text-reef-100"
                        data-testid={`calendar-edit-${e.id}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteEvent(e.id)}
                        className="text-xs text-coral-400 hover:text-coral-500"
                        data-testid={`calendar-delete-${e.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {editor ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          data-testid="calendar-editor-modal"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editor.mode === 'add' ? 'Add event' : 'Edit event'}
              </CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Title
                <input
                  type="text"
                  className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.event.title}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      event: { ...editor.event, title: ev.target.value },
                    })
                  }
                  data-testid="calendar-editor-title"
                  placeholder="Weekly water change"
                />
              </label>

              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Category
                <select
                  className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.event.category}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      event: {
                        ...editor.event,
                        category: ev.target.value as CalendarCategory,
                      },
                    })
                  }
                  data-testid="calendar-editor-category"
                >
                  {CATEGORY_LABELS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>

              <RecurrenceEditor
                value={editor.event.recurrence}
                onChange={(recurrence: Recurrence) =>
                  setEditor({
                    ...editor,
                    event: { ...editor.event, recurrence },
                  })
                }
              />

              <label className="flex flex-col gap-1 text-xs text-reef-300">
                Time (optional)
                <input
                  type="time"
                  className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
                  value={editor.event.time ?? ''}
                  onChange={(ev) =>
                    setEditor({
                      ...editor,
                      event: {
                        ...editor.event,
                        time: ev.target.value || undefined,
                      },
                    })
                  }
                  data-testid="calendar-editor-time"
                />
              </label>

              <div className="mt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="rounded-md border border-reef-700 bg-reef-900/60 px-3 py-1.5 text-sm text-reef-200 hover:bg-reef-800"
                  data-testid="calendar-editor-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveEditor}
                  disabled={editor.event.title.trim().length === 0}
                  className="reefapp-btn-secondary text-sm disabled:opacity-50"
                  data-testid="calendar-editor-save"
                >
                  Save
                </button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
